import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AppShell } from '@/shared/ui/AppShell'
import { Skeleton } from '@/shared/ui/Skeleton'
import { ErrorState } from '@/shared/ui/States'
import { HttpError } from '@/shared/api/http'
import { useLatestIntel } from '../state/useLatestIntel'
import { IntelEmptyState } from './IntelEmptyState'
import { IntelKpiStrip } from './IntelKpiStrip'
import { IntelBriefSummary } from './IntelBriefSummary'
import { IntelActionItems } from './IntelActionItems'
import { IntelTopicClusters } from './IntelTopicClusters'
import { IntelCommentBreakdown } from './IntelCommentBreakdown'
import { IntelExportButton } from './IntelExportButton'

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </h2>
  )
}

function IntelSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-32 rounded-xl" />
      <Skeleton className="h-48 rounded-xl" />
      <Skeleton className="h-40 rounded-xl" />
      <Skeleton className="h-32 rounded-xl" />
    </div>
  )
}

export function IntelPage() {
  const { topicId = '' } = useParams<{ topicId: string }>()
  const { t } = useTranslation('intel')
  const { data, isLoading, error } = useLatestIntel(topicId)

  const isNotFound = error instanceof HttpError && error.status === 404

  return (
    <AppShell showAnalytics={false} showTopBar>
      <div className="flex flex-col gap-6 px-4 py-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-foreground">{t('title')}</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">{t('subtitle')}</p>
          </div>
          <IntelExportButton />
        </div>

        {isLoading && <IntelSkeleton />}

        {!isLoading && error != null && !isNotFound && <ErrorState error={error} />}

        {!isLoading && (isNotFound || (error == null && data == null)) && (
          <IntelEmptyState />
        )}

        {!isLoading && data != null && (
          <div className="flex flex-col gap-6">
            <section>
              <SectionHeading>{t('section.kpi')}</SectionHeading>
              <IntelKpiStrip kpi={data.kpi} />
            </section>

            <section>
              <SectionHeading>{t('section.brief')}</SectionHeading>
              <IntelBriefSummary
                brief={data.brief}
                generatedAt={data.generatedAt}
                model={data.model}
              />
            </section>

            <section>
              <SectionHeading>{t('section.actions')}</SectionHeading>
              <IntelActionItems items={data.actionItems} />
            </section>

            <section>
              <SectionHeading>{t('section.clusters')}</SectionHeading>
              <IntelTopicClusters clusters={data.topicClusters} />
            </section>

            <section>
              <SectionHeading>{t('section.breakdown')}</SectionHeading>
              <IntelCommentBreakdown breakdown={data.commentBreakdown} />
            </section>
          </div>
        )}
      </div>
    </AppShell>
  )
}
